import { GridApi } from "ag-grid-community";
import { useCallback, useReducer } from "react";
import { IAttribute } from "../../../services/attribute-data-service/Models";
import { IUserSettings, UIShortcutEnum } from "../../../services/user-settings-data/Models";

type DevicesViewStateType = {
    // Options state
    configurationID: number;
    filterText: string;

    groupByID: number;
    attributeOptions: IAttribute[],
    page: number;

    gridApi: GridApi | undefined,

    // Other state
    defaultConfigurationID: number;
    isNoData: boolean;
    isOptionsDirty: boolean;
    refreshing: boolean;

    confiurationError?: boolean

    summaryListingDisplayFunctionIcons: boolean;
    summaryListingAlarmAtTop: boolean;
    summaryListingOutletsFirst: boolean;

    initialSummaryListingDisplayFunctionIcons?: boolean;
    initialSummaryListingAlarmAtTop?: boolean;
    initialSummaryListingOutletsFirst?: boolean;


    historicalData: boolean;
    graph: boolean;
    marketingReport: boolean;
    plungerAnalysis: boolean;
    gaugeSheet: boolean;
    dynacard: boolean;
    addMemo: boolean;
    mapIt: boolean;


    initialHistoricalData?: boolean;
    initialGraph?: boolean;
    initialMarketingReport?: boolean;
    initialPlungerAnalysis?: boolean;
    initialGaugesheet?: boolean;
    initialDynacard?: boolean;
    initialAddMemo?: boolean;
    initialMapIt?: boolean;

};

const initialState: DevicesViewStateType = {
    configurationID: -1,
    defaultConfigurationID: -1,
    filterText: "",
    groupByID: -1,
    page: 1,
    isNoData: false,
    isOptionsDirty: false,
    gridApi: undefined,
    attributeOptions: [],
    refreshing: false,
    confiurationError: false,

    summaryListingDisplayFunctionIcons: false,
    summaryListingAlarmAtTop: false,
    summaryListingOutletsFirst: false,

    historicalData: false,
    graph: false,
    marketingReport: false,
    plungerAnalysis: false,
    gaugeSheet: false,
    dynacard: false,
    addMemo: false,
    mapIt: false,

};

export enum DevicesViewActionEnum {
    ResetOptionsRequested = "RESET_OPTIONS_REQUESTED",
    ConfigurationChanged = "CONFIGURATION_CHANGED",
    DefaultConfigurationChanged = "DEFAULT_CONFIGURATION_CHANGED",
    FilterTextChanged = "FILTER_TEXT_CHANGED",
    NoDataChanged = "NO_DATA_CHANGED",
    GridApiChanged = 'GRID_API_CHANGED',
    GroupByIDChanged = 'GROUPBY_ID_CHANGED',
    PageChanged = 'PAGE_CHANGED',
    AttributeOptionsChanged = 'ATTRIBUTE_OPTIONS_CHANGED',
    RefreshingChanged = "REFRESHING_CHANGED",
    ConfigurationErrorChanged = "DEVICES_VIEW_CONFIGURATION_ERROR_CHANGED",
    DisplayFunctionIconsChanged = "DEVICES_VIEW_DISPLAY_FUNCTION_ICONS",
    DisplayAlarmsAtTopChanged = "DEVICES_VIEW_ALARMS_AT_TOP",
    DisplayOutletsFirstChanged = "DEVICES_VIEW_OUTLETS_FIRST",

    DisplayHistoricalDataShortcut = "DEVICES_VIEW_DISPLAY_HISTORICAL_DATA_SHORTCUT",
    DisplayGraphShortcut = "DEVICES_VIEW_DISPLAY_GRAPH_SHORTCUT",
    DisplayMarketingReportShortcut = "DEVICES_VIEW_DISPLAY_MARKETING_REPORT_SHORTCUT",
    DisplayPlungerAnalysisShortcut = "DEVICES_VIEW_DISPLAY_PLUNGER_ANALYSIS_SHORTCUT",
    DisplayGaugeSheetShortcut = "DEVICES_VIEW_DISPLAY_GAUGESHEET_SHORTCUT",
    DisplayDynacardShortcut = "DEVICES_VIEW_DISPLAY_DYNACARD_SHORTCUT",
    DisplayAddMemoShortcut = "DEVICES_VIEW_DISPLAY_ADD_MEMO_SHORTCUT",
    DisplayMapItShortcut = "DEVICES_VIEW_DISPLAY_MAPIT_SHORTCUT",
}

type DevicesViewActionTypes = {
    type: DevicesViewActionEnum.ResetOptionsRequested;
    payload: IUserSettings
} | {
    type: DevicesViewActionEnum.ConfigurationChanged;
    configurationIDOrCallback: number | ((configurationID: number) => number);
} | {
    type: DevicesViewActionEnum.DefaultConfigurationChanged;
    defaultConfigurationID: number;
} | {
    type: DevicesViewActionEnum.FilterTextChanged;
    filterText: string;
} | {
    type: DevicesViewActionEnum.GridApiChanged;
    payload: GridApi;
} | {
    type: DevicesViewActionEnum.GroupByIDChanged;
    payload: number;
} | {
    type: DevicesViewActionEnum.PageChanged;
    payload: number;
} | {
    type: DevicesViewActionEnum.AttributeOptionsChanged;
    payload: IAttribute[]
} | {
    type: DevicesViewActionEnum.NoDataChanged;
    isNoData: boolean;
} | {
    type: DevicesViewActionEnum.ConfigurationErrorChanged;
    payload: boolean;
} | {
    type: DevicesViewActionEnum.DisplayFunctionIconsChanged;
    payload: boolean;
} | {
    type: DevicesViewActionEnum.DisplayAlarmsAtTopChanged;
    payload: boolean;
} | {
    type: DevicesViewActionEnum.DisplayOutletsFirstChanged;
    payload: boolean;
} | {
    type: DevicesViewActionEnum.RefreshingChanged;
    payload: boolean;
} | {
    type: DevicesViewActionEnum.DisplayHistoricalDataShortcut;
    payload: boolean;
} | {
    type: DevicesViewActionEnum.DisplayGraphShortcut;
    payload: boolean;
} | {
    type: DevicesViewActionEnum.DisplayMarketingReportShortcut;
    payload: boolean;
} | {
    type: DevicesViewActionEnum.DisplayPlungerAnalysisShortcut;
    payload: boolean;
} | {
    type: DevicesViewActionEnum.DisplayGaugeSheetShortcut;
    payload: boolean;
} | {
    type: DevicesViewActionEnum.DisplayDynacardShortcut;
    payload: boolean;
} | {
    type: DevicesViewActionEnum.DisplayAddMemoShortcut;
    payload: boolean;                        
} | {
    type: DevicesViewActionEnum.DisplayMapItShortcut;
    payload: boolean;                        
};

function reducer(state: DevicesViewStateType, action: DevicesViewActionTypes): DevicesViewStateType {

    const copy = { ...state };
    switch (action.type) {

        case DevicesViewActionEnum.ResetOptionsRequested:
            copy.configurationID = state.defaultConfigurationID;
            copy.filterText = initialState.filterText;
            copy.groupByID = initialState.groupByID;
            copy.isOptionsDirty = false;
            copy.page = initialState.page;

            copy.summaryListingDisplayFunctionIcons = action.payload.summaryListingDisplayFunctionIcons;
            copy.summaryListingAlarmAtTop = action.payload.summaryListingAlarmAtTop;
            copy.summaryListingOutletsFirst = action.payload.summaryListingOutletsFirst;

            copy.historicalData = ((action.payload.uiShortcutMask ?? 0) & UIShortcutEnum.HistoricalData) > 0;
            copy.graph = ((action.payload.uiShortcutMask ?? 0) & UIShortcutEnum.Graph) > 0;
            copy.marketingReport = ((action.payload.uiShortcutMask ?? 0) & UIShortcutEnum.MarketingReport) > 0;
            copy.plungerAnalysis = ((action.payload.uiShortcutMask ?? 0) & UIShortcutEnum.PlungerAnalysis) > 0;
            copy.gaugeSheet = ((action.payload.uiShortcutMask ?? 0) & UIShortcutEnum.GaugeSheet) > 0;
            copy.dynacard = ((action.payload.uiShortcutMask ?? 0) & UIShortcutEnum.Dynacard) > 0;
            copy.addMemo = ((action.payload.uiShortcutMask ?? 0) & UIShortcutEnum.AddMemo) > 0;
            copy.mapIt = ((action.payload.uiShortcutMask ?? 0) & UIShortcutEnum.MapIt) > 0;
            return copy;

        case DevicesViewActionEnum.ConfigurationChanged:
            copy.configurationID = typeof action.configurationIDOrCallback === "function"
                ? action.configurationIDOrCallback(state.configurationID)
                : action.configurationIDOrCallback
            copy.groupByID = initialState.groupByID;            
            copy.isOptionsDirty = isOptionsDirty(copy);
            copy.page = initialState.page;
            return copy;

        case DevicesViewActionEnum.FilterTextChanged:
            copy.filterText = action.filterText;
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case DevicesViewActionEnum.DefaultConfigurationChanged:
            initialState.groupByID = -1;
            copy.defaultConfigurationID = action.defaultConfigurationID;
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case DevicesViewActionEnum.NoDataChanged:
            copy.isNoData = action.isNoData;
            return copy;

        case DevicesViewActionEnum.GridApiChanged:
            copy.gridApi = action.payload;
            return copy;

        case DevicesViewActionEnum.GroupByIDChanged:

            if (initialState.groupByID === - 1 && copy.configurationID === copy.defaultConfigurationID) {
                initialState.groupByID = action.payload;
            }

            copy.groupByID = action.payload;
            copy.page = initialState.page;            
            copy.isOptionsDirty = isOptionsDirty(copy);            
            return copy;
    
        case DevicesViewActionEnum.PageChanged:
            copy.page = action.payload;
            return copy;

        case DevicesViewActionEnum.AttributeOptionsChanged:
            copy.attributeOptions = action.payload;
            return copy;

        case DevicesViewActionEnum.RefreshingChanged:
            copy.refreshing = action.payload;
            return copy;

        case DevicesViewActionEnum.ConfigurationErrorChanged:
            copy.confiurationError = action.payload;
            return copy;

        case DevicesViewActionEnum.DisplayFunctionIconsChanged:
            copy.summaryListingDisplayFunctionIcons = action.payload;
            if (copy.initialSummaryListingDisplayFunctionIcons === undefined) {
                copy.initialSummaryListingDisplayFunctionIcons = action.payload;
            }
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case DevicesViewActionEnum.DisplayAlarmsAtTopChanged:
            copy.summaryListingAlarmAtTop = action.payload;
            if (copy.initialSummaryListingAlarmAtTop === undefined) {
                copy.initialSummaryListingAlarmAtTop = action.payload;
            }
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case DevicesViewActionEnum.DisplayOutletsFirstChanged:
            copy.summaryListingOutletsFirst = action.payload;
            if (copy.initialSummaryListingOutletsFirst === undefined) {
                copy.initialSummaryListingOutletsFirst = action.payload;
            }
            copy.isOptionsDirty = isOptionsDirty(copy);

            return copy;

        case DevicesViewActionEnum.DisplayHistoricalDataShortcut:
            copy.historicalData = action.payload;
            if (copy.initialHistoricalData === undefined) {
                copy.initialHistoricalData = action.payload;
            }

            copy.isOptionsDirty = isOptionsDirty(copy);
            break;

        case DevicesViewActionEnum.DisplayGraphShortcut:
            copy.graph = action.payload;
            if (copy.initialGraph === undefined) {
                copy.initialGraph = action.payload;
            }

            copy.isOptionsDirty = isOptionsDirty(copy);
            break;

        case DevicesViewActionEnum.DisplayMarketingReportShortcut:
            copy.marketingReport = action.payload;
            if (copy.initialMarketingReport === undefined) {
                copy.initialMarketingReport = action.payload;
            }

            copy.isOptionsDirty = isOptionsDirty(copy);
            break;

        case DevicesViewActionEnum.DisplayPlungerAnalysisShortcut:
            copy.plungerAnalysis = action.payload;
            if (copy.initialPlungerAnalysis === undefined) {
                copy.initialPlungerAnalysis = action.payload;
            }

            copy.isOptionsDirty = isOptionsDirty(copy);
            break;

        case DevicesViewActionEnum.DisplayGaugeSheetShortcut:
            copy.gaugeSheet = action.payload;
            if (copy.initialGaugesheet === undefined) {
                copy.initialGaugesheet = action.payload;
            }

            copy.isOptionsDirty = isOptionsDirty(copy);
            break;

        case DevicesViewActionEnum.DisplayDynacardShortcut:
            copy.dynacard = action.payload;
            if (copy.initialDynacard === undefined) {
                copy.initialDynacard = action.payload;
            }

            copy.isOptionsDirty = isOptionsDirty(copy);
            break;


            case DevicesViewActionEnum.DisplayAddMemoShortcut:
            copy.addMemo = action.payload;
            if (copy.initialAddMemo === undefined) {
                copy.initialAddMemo = action.payload;
            }

            copy.isOptionsDirty = isOptionsDirty(copy);
            break;
        case DevicesViewActionEnum.DisplayMapItShortcut:
            copy.mapIt = action.payload;
            if (copy.initialMapIt === undefined) {
                copy.initialMapIt = action.payload;
            }

            copy.isOptionsDirty = isOptionsDirty(copy);
            break;

        default:
            return state;
    }
    return copy;
}

function isOptionsDirty(state: DevicesViewStateType) {
    return state.configurationID !== state.defaultConfigurationID ||
        state.filterText !== initialState.filterText ||
        state.groupByID !== initialState.groupByID ||
        (state.initialSummaryListingDisplayFunctionIcons !== undefined && state.initialSummaryListingDisplayFunctionIcons !== state.summaryListingDisplayFunctionIcons) ||
        (state.initialSummaryListingAlarmAtTop !== undefined && state.initialSummaryListingAlarmAtTop !== state.summaryListingAlarmAtTop) ||
        (state.initialSummaryListingOutletsFirst !== undefined && state.initialSummaryListingOutletsFirst !== state.summaryListingOutletsFirst) ||
        (state.initialHistoricalData !== undefined && state.initialHistoricalData !== state.historicalData) ||        
        (state.initialGraph !== undefined && state.initialGraph !== state.graph) ||                
        (state.initialMarketingReport !== undefined && state.initialMarketingReport !== state.marketingReport) ||                        
        (state.initialPlungerAnalysis !== undefined && state.initialPlungerAnalysis !== state.plungerAnalysis) ||                                
        (state.initialGaugesheet !== undefined && state.initialGaugesheet !== state.gaugeSheet) ||                                        
        (state.initialDynacard !== undefined && state.initialDynacard !== state.dynacard) ||                                        
        (state.initialAddMemo !== undefined && state.initialAddMemo !== state.addMemo) ||                                                
        (state.initialMapIt !== undefined && state.initialMapIt !== state.mapIt)
        ;
}

type UseDevicesViewStateType = {
    state: DevicesViewStateType;
    resetOptions: (userSettings: IUserSettings) => void;
    setConfigurationID: (configurationIDOrCallback: number | ((configurationID: number) => number)) => void;
    setFilterText: (filterText: string) => void;
    setDefaultConfigurationID: (defaultConfigurationID: number) => void;
    setNoData: (isNoData: boolean) => void;
    setGridApi: (value: GridApi) => void;
    setGroupByID: (value: number) => void;
    setPage: (value: number) => void;
    setAttributeOptions: (value: IAttribute[]) => void;
    setRefreshing: (value: boolean) => void;
    setConfigurationError: (value: boolean) => void;
    setFunctionIcons: (value: boolean) => void;
    setAlarmsAtTop: (value: boolean) => void;
    setOutletsFirst: (value: boolean) => void;
    setShowHistoricalDataShortcut: (value: boolean) => void;
    setShowGraphShortcut: (value: boolean) => void;
    setShowMarketinReportShortcut: (value: boolean) => void;
    setShowPlungerAnalysisShortcut: (value: boolean) => void;
    setShowGaugesheetShortcut: (value: boolean) => void;
    setShowDynacardShortcut: (value: boolean) => void;
    setShowAddMemoShortcut: (value: boolean) => void;
    setShowMapItShortcut: (value: boolean) => void;
};

export default function useDevicesViewState(): UseDevicesViewStateType {
    const [state, dispatch] = useReducer(reducer, initialState);

    const resetOptions = useCallback((value: IUserSettings) => dispatch({
            type: DevicesViewActionEnum.ResetOptionsRequested,
            payload: value
        }), []);

    const setConfigurationID = useCallback((configurationIDOrCallback: number | ((configurationID: number) => number)) => dispatch({
            type: DevicesViewActionEnum.ConfigurationChanged,
            configurationIDOrCallback: configurationIDOrCallback
        }), []);

    const setFilterText = useCallback((filterText: string) => dispatch({
            type: DevicesViewActionEnum.FilterTextChanged,
            filterText: filterText
        }), []);

    const setDefaultConfigurationID = useCallback((configurationID: number) => dispatch({
            type: DevicesViewActionEnum.DefaultConfigurationChanged,
            defaultConfigurationID: configurationID
        }), []);

    const setNoData = useCallback((isNoData: boolean) => dispatch({
            type: DevicesViewActionEnum.NoDataChanged,
            isNoData: isNoData
        }), []);

    const setGridApi = useCallback((value: GridApi) => dispatch({
        type: DevicesViewActionEnum.GridApiChanged,
        payload: value
    }), []);

    const setGroupByID = useCallback((value: number) => dispatch({
        type: DevicesViewActionEnum.GroupByIDChanged,
        payload: value
    }), []);

    const setPage = useCallback((value: number) => dispatch({
        type: DevicesViewActionEnum.PageChanged,
        payload: value
    }), []);

    const setAttributeOptions = useCallback((value: IAttribute[]) => dispatch({
        type: DevicesViewActionEnum.AttributeOptionsChanged,
        payload: value
    }), []);

    const setRefreshing = useCallback((value: boolean) => dispatch({
        type: DevicesViewActionEnum.RefreshingChanged,
        payload: value
    }), []);

    const setConfigurationError = useCallback((value: boolean) => dispatch({
        type: DevicesViewActionEnum.ConfigurationErrorChanged,
        payload: value
    }), []);

    const setFunctionIcons = useCallback((value: boolean) => dispatch({
        type: DevicesViewActionEnum.DisplayFunctionIconsChanged,
        payload: value
    }), []);


    const setAlarmsAtTop = useCallback((value: boolean) => dispatch({
        type: DevicesViewActionEnum.DisplayAlarmsAtTopChanged,
        payload: value
    }), []);


    const setOutletsFirst = useCallback((value: boolean) => dispatch({
        type: DevicesViewActionEnum.DisplayOutletsFirstChanged,
        payload: value
    }), []);


    const setShowHistoricalDataShortcut = useCallback((value: boolean) => dispatch({
        type: DevicesViewActionEnum.DisplayHistoricalDataShortcut,
        payload: value
    }), []);

    const setShowGraphShortcut = useCallback((value: boolean) => dispatch({
        type: DevicesViewActionEnum.DisplayGraphShortcut,
        payload: value
    }), []);
    const setShowMarketinReportShortcut = useCallback((value: boolean) => dispatch({
        type: DevicesViewActionEnum.DisplayMarketingReportShortcut,
        payload: value
    }), []);
    const setShowPlungerAnalysisShortcut = useCallback((value: boolean) => dispatch({
        type: DevicesViewActionEnum.DisplayPlungerAnalysisShortcut,
        payload: value
    }), []);
    const setShowGaugesheetShortcut = useCallback((value: boolean) => dispatch({
        type: DevicesViewActionEnum.DisplayGaugeSheetShortcut,
        payload: value
    }), []);
    const setShowDynacardShortcut = useCallback((value: boolean) => dispatch({
        type: DevicesViewActionEnum.DisplayDynacardShortcut,
        payload: value
    }), []);
    const setShowAddMemoShortcut = useCallback((value: boolean) => dispatch({
        type: DevicesViewActionEnum.DisplayAddMemoShortcut,
        payload: value
    }), []);
    const setShowMapItShortcut = useCallback((value: boolean) => dispatch({
        type: DevicesViewActionEnum.DisplayMapItShortcut,
        payload: value
    }), []);

    return {
        state,
        resetOptions,
        setConfigurationID,
        setFilterText,
        setDefaultConfigurationID,
        setNoData,
        setGridApi,
        setGroupByID,
        setPage,
        setAttributeOptions,
        setRefreshing,
        setConfigurationError,
        setFunctionIcons,
        setAlarmsAtTop,
        setOutletsFirst,
        setShowHistoricalDataShortcut,
        setShowGraphShortcut,
        setShowMarketinReportShortcut,
        setShowPlungerAnalysisShortcut,
        setShowGaugesheetShortcut,
        setShowDynacardShortcut,
        setShowAddMemoShortcut,
        setShowMapItShortcut

    };
}    
